import SearchField from 'components/SearchField';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import PageFilters, { Filter } from 'components/PageFilters';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';
import { InvestmentPaymentStatus } from 'further-types/investment';
import { InvestmentPaymentStatusLabels } from 'further-ui/labels';
import AppSelectBox from 'components/FormElements/AppSelectBox';

type FiltersType = {
  firmId?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  paymentStatuses?: Array<InvestmentPaymentStatus>;
};

type Props = {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
};

const paymentStatusOptions = Object.values(InvestmentPaymentStatus).map(
  (status) => ({
    value: status,
    label: InvestmentPaymentStatusLabels[status],
  }),
);

const BulkPaymentAssignmentFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
}) => {
  const { isSuperAdmin } = useGetRole();

  return (
    <PageFilters pageId="bulk-payment-assignment">
      {isSuperAdmin && (
        <Filter>
          <FirmSelector
            placeholder="Select firm"
            firmId={filters.firmId}
            onChange={(firmId) => {
              onFiltersChange({ firmId });
            }}
          />
        </Filter>
      )}
      <Filter>
        <SearchField
          variant="outlined"
          placeholder="Search by email, subscription ID or investor name"
          allowEmptyQuery
          autoSearchOnDebounce
          onSearch={(search: string) => onFiltersChange({ search })}
          fullWidth
        />
      </Filter>
      <Filter>
        <MuiDateRangePicker
          dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
          onDateRangeChange={({ startDate, endDate }) =>
            onFiltersChange({ startDate, endDate })
          }
          placeholder="Filter by subscription date"
        />
      </Filter>
      <Filter>
        <AppSelectBox
          multiple
          fullWidth
          valueKey="value"
          labelKey="label"
          placeholder="Payment status"
          variant="outlined"
          data={paymentStatusOptions}
          value={filters.paymentStatuses ?? []}
          onChange={(event) => {
            const selected = event.target.value as Array<string>;
            if (selected.includes('all')) {
              const allSelected =
                (filters.paymentStatuses?.length ?? 0) ===
                paymentStatusOptions.length;
              onFiltersChange({
                paymentStatuses: allSelected
                  ? []
                  : paymentStatusOptions.map((o) => o.value),
              });
            } else {
              onFiltersChange({
                paymentStatuses: selected.filter(
                  Boolean,
                ) as Array<InvestmentPaymentStatus>,
              });
            }
          }}
          renderValue={(selected) => {
            const values = (selected as Array<InvestmentPaymentStatus>).filter(
              Boolean,
            );
            if (values.length === 0) return 'Payment status';
            return values
              .map((v) => InvestmentPaymentStatusLabels[v])
              .join(', ');
          }}
        />
      </Filter>
    </PageFilters>
  );
};

export default BulkPaymentAssignmentFilters;
